import React, { useState, useEffect } from 'react';
import { 
  deleteUser, 
  EmailAuthProvider, 
  GoogleAuthProvider, 
  reauthenticateWithCredential, 
  signInWithPopup,
  updatePassword 
} from 'firebase/auth';
import { auth } from '../firebase';
import './Settings.css';

const Settings = ({ isOpen, onClose }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState(''); // Added missing state
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'auto');

  // Cambia las variables CSS según el tema
  useEffect(() => {
    const applyTheme = (selectedTheme) => {
      let themeToApply = selectedTheme;
      if (selectedTheme === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        themeToApply = prefersDark ? 'dark' : 'light';
      }
      if (themeToApply === 'dark') {
        document.documentElement.style.setProperty('--header-color', '#222');
        document.documentElement.style.setProperty('--text-color', '#000'); 
        document.body.style.background = '#181818';
    } else {
        document.documentElement.style.setProperty('--header-color', '#fff');
        document.documentElement.style.setProperty('--text-color', '#000'); 
        document.body.style.background = '#f5f5f5';
    }

      // NO toques --button-color aquí
    };

    applyTheme(theme);
    localStorage.setItem('theme', theme);

    // Si es automático, escucha cambios del sistema
    let mediaQuery;
    if (theme === 'auto') {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme('auto');
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [theme]);

  const handleChangePassword = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      if (newPassword !== confirmPassword) {
        setPasswordError("Las contraseñas no coinciden");
        return;
      }

      if (newPassword.length < 6) {
        setPasswordError("La contraseña debe tener al menos 6 caracteres");
        return;
      }

      // Re-autenticar usuario
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Actualizar contraseña
      await updatePassword(user, newPassword);

      // Limpiar campos y cerrar diálogo
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowChangePassword(false);
      setPasswordError(null);
    } catch (error) {
      console.error("Error changing password:", error);
      if (error.code === 'auth/wrong-password') {
        setPasswordError("Contraseña actual incorrecta");
      } else {
        setPasswordError("Error al cambiar la contraseña");
      }
    }
  };

  const renderChangePassword = () => {
    const user = auth.currentUser;
    const isGoogleUser = user?.providerData[0].providerId === 'google.com';

    if (isGoogleUser) {
      return (
        <div className="password-message">
          <p>No puedes cambiar la contraseña porque iniciaste sesión con Google</p>
        </div>
      );
    }

    return (
      <div className="change-password-form">
        <div className="password-input">
          <label>Contraseña actual:</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Introduce tu contraseña actual"
          />
        </div>
        <div className="password-input">
          <label>Nueva contraseña:</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Introduce la nueva contraseña"
          />
        </div>
        <div className="password-input">
          <label>Confirmar nueva contraseña:</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirma la nueva contraseña"
          />
        </div>
        {passwordError && <p className="error-message">{passwordError}</p>}
        <div className="password-buttons">
          <button 
            className="settings-button primary"
            onClick={handleChangePassword}
          >
            Cambiar contraseña
          </button>
          <button 
            className="settings-button"
            onClick={() => {
              setShowChangePassword(false);
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
              setPasswordError(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteAccount = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const isGoogleUser = user.providerData[0].providerId === 'google.com';

      if (isGoogleUser) {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
      } else {
        if (!deletePassword) { // Changed from password to deletePassword
          setDeleteError("Por favor, introduce tu contraseña");
          return;
        }
        const credential = EmailAuthProvider.credential(
          user.email, 
          deletePassword // Changed from password to deletePassword
        );
        await reauthenticateWithCredential(user, credential);
      }

      await deleteUser(user);
      onClose();
    } catch (error) {
      console.error("Error deleting account:", error);
      if (error.code === 'auth/wrong-password') {
        setDeleteError("Contraseña incorrecta");
      } else {
        setDeleteError("Error al eliminar la cuenta. Por favor, inténtalo de nuevo.");
      }
    }
  };

  const renderDeleteConfirmation = () => {
    const user = auth.currentUser;
    const isGoogleUser = user?.providerData[0].providerId === 'google.com';

    return (
      <div className="delete-confirm">
        <p>¿Estás seguro? Esta acción no se puede deshacer.</p>
        {deleteError && <p className="error-message">{deleteError}</p>}
        
        {!isGoogleUser && (
          <div className="password-input">
            <label>Confirma tu contraseña:</label>
            <input
              type="password"
              value={deletePassword} // Changed from password to deletePassword
              onChange={(e) => setDeletePassword(e.target.value)} // Changed from setPassword
              placeholder="Introduce tu contraseña"
            />
          </div>
        )}

        <div className="delete-buttons">
          <button 
            className="settings-button danger"
            onClick={handleDeleteAccount}
          >
            {isGoogleUser ? 'Confirmar con Google y eliminar' : 'Sí, eliminar mi cuenta'}
          </button>
          <button 
            className="settings-button"
            onClick={() => {
              setShowDeleteConfirm(false);
              setDeletePassword(''); // Changed from setPassword
              setDeleteError(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay">
      <div className="settings-popup">
        <div className="settings-header">
          <h2>Ajustes</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="settings-content">
          <div className="settings-section">
            <h3>Tema</h3>
            <select
              className="theme-select"
              value={theme}
              onChange={e => setTheme(e.target.value)}
            >
              <option value="auto">Automático (según el sistema)</option>
              <option value="light">Claro</option>
              <option value="dark">Oscuro</option>
            </select>
          </div>

          <div className="settings-section">
            <h3>Cuenta</h3>
            {!showChangePassword ? (
              <button 
                className="settings-button"
                onClick={() => setShowChangePassword(true)}
              >
                Cambiar contraseña
              </button>
            ) : renderChangePassword()}
            
            {!showDeleteConfirm ? (
              <button 
                className="settings-button danger"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Eliminar cuenta
              </button>
            ) : renderDeleteConfirmation()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;